import {
  menu,
  popFilterableSimpleMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { signal } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import {
  wizardActionsStyle,
  wizardAdvancedHeaderTextStyle,
  wizardAdvancedTriggerStyle,
  wizardBackdropStyle,
  wizardChoiceCardStyle,
  wizardChoiceRowStyle,
  wizardContainedBackdropStyle,
  wizardContainedDialogStyle,
  wizardCreateCancelStyle,
  wizardCreateInputRowStyle,
  wizardCreateInputStyle,
  wizardDescriptionStyle,
  wizardDialogStyle,
  wizardDisclosureButtonStyle,
  wizardExpandableBodyStyle,
  wizardExpandableInnerStyle,
  wizardFieldStyle,
  wizardFoundBadgeStyle,
  wizardHeroStyle,
  wizardHintStyle,
  wizardInlineContainerStyle,
  wizardLabelStyle,
  wizardPrimaryButtonStyle,
  wizardSecondaryButtonStyle,
  wizardSectionHeaderStyle,
  wizardSectionHintStyle,
  wizardSectionStyle,
  wizardSectionTitleStyle,
  wizardSelectPlaceholderStyle,
  wizardSelectStyle,
  wizardTitleStyle,
  wizardToggleStyle,
  wizardToggleThumbStyle,
  wizardTrackingRowStyle,
} from '../../../shared/setup-wizard/setup-wizard-css.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import {
  ColumnDetectionService,
  type DetectedColumns,
} from './column-detection-service.js';
import { applyGanttSetup } from './gantt-setup-apply.js';

export const GANTT_SETUP_WIZARD_LAYOUT_CHANGE_EVENT =
  'gantt-setup-wizard-layout-change';

declare global {
  interface HTMLElementEventMap {
    [GANTT_SETUP_WIZARD_LAYOUT_CHANGE_EVENT]: CustomEvent<null>;
  }
}

/**
 * User-visible copy shown by the setup wizard. Callers wire their own
 * i18n layer by passing a {@link strings} property that overrides any
 * subset of keys — anything left undefined falls back to the English
 * defaults shipped with the component. Functions are used for strings
 * that interpolate column names / role labels so translation order is
 * the caller's concern, not ours.
 */
export interface GanttWizardStrings {
  readonly title: string;
  readonly description: string;
  readonly automaticSummary: string;
  readonly advancedTitle: string;
  readonly advancedDescription: string;
  readonly datesTitle: string;
  readonly datesAutoHint: string;
  readonly datesFoundHint: (names: string) => string;
  readonly startLabel: string;
  readonly endLabel: string;
  readonly chooseColumnPlaceholder: string;
  readonly oneDateHint: (columnName: string) => string;
  readonly roleStart: string;
  readonly roleEnd: string;
  readonly missingColumnNote: (missing: string) => string;
  readonly trackingTitle: string;
  readonly progressTitle: string;
  readonly progressDescription: string;
  readonly progressAutoHint: string;
  readonly progressFoundHint: (columnName: string) => string;
  readonly progressColumnLabel: string;
  readonly statusTitle: string;
  readonly statusDescription: string;
  readonly statusAutoHint: string;
  readonly statusFoundHint: (columnName: string) => string;
  readonly statusColumnLabel: string;
  readonly assigneeTitle: string;
  readonly assigneeDescription: string;
  readonly assigneeAutoHint: string;
  readonly assigneeFoundHint: (columnName: string) => string;
  readonly assigneeColumnLabel: string;
  readonly dependenciesTitle: string;
  readonly dependenciesDescription: string;
  readonly dependenciesAutoHint: string;
  readonly dependenciesFoundHint: (columnName: string) => string;
  readonly dependenciesColumnLabel: string;
  readonly dependenciesUnavailable: string;
  readonly foundBadge: string;
  readonly matchedSuffix: string;
  readonly cancel: string;
  readonly apply: string;
  readonly getStarted: string;
  readonly createNewColumn: string;
  readonly newColumnNamePlaceholder: string;
  readonly chooseOrCreatePlaceholder: string;
  readonly autoCreateLabel: (defaultName: string) => string;
}

const DEFAULT_STRINGS: GanttWizardStrings = {
  title: 'Plan your tasks on a timeline',
  description:
    "We'll create a complete Gantt setup. Advanced settings let you reuse existing columns.",
  automaticSummary:
    'Start, End, Progress, Status, Assignee and Dependencies are configured automatically.',
  advancedTitle: 'Advanced settings',
  advancedDescription: 'Use existing columns or choose custom names.',
  datesTitle: 'When do tasks start and end?',
  datesAutoHint: "We'll create Start and End columns for you.",
  datesFoundHint: names =>
    `We spotted ${names} — we'll use ${
      names.includes(' and ') ? 'those' : 'that'
    }.`,
  startLabel: 'Start date',
  endLabel: 'End date',
  chooseColumnPlaceholder: 'Choose a column…',
  oneDateHint: name =>
    `You have one date column: ${name}. What does it represent?`,
  roleStart: 'Start date',
  roleEnd: 'End date',
  missingColumnNote: missing =>
    `We'll add a ${missing} column for the other side.`,
  trackingTitle: 'Status tracking',
  progressTitle: 'Track progress',
  progressDescription: 'Show a fill on each bar for how far along the task is.',
  progressAutoHint: "We'll add a Progress column for you.",
  progressFoundHint: name => `Found "${name}" — we'll use that.`,
  progressColumnLabel: 'Progress',
  statusTitle: 'Track status',
  statusDescription:
    'Pick a status per task (Todo, In Progress, Done…) to colour bars.',
  statusAutoHint: "We'll add an empty Status column — you pick the labels.",
  statusFoundHint: name => `Found "${name}" — we'll use that.`,
  statusColumnLabel: 'Status',
  assigneeTitle: 'Assign tasks',
  assigneeDescription: 'Show who is responsible for each task.',
  assigneeAutoHint: "We'll add an Assignee column for you.",
  assigneeFoundHint: name => `Found "${name}" — we'll use that.`,
  assigneeColumnLabel: 'Assignee',
  dependenciesTitle: 'Dependencies',
  dependenciesDescription: 'Draw arrows between tasks that block each other.',
  dependenciesAutoHint: "We'll add a Dependencies column for you.",
  dependenciesFoundHint: name => `Found "${name}" — we'll use that.`,
  dependenciesColumnLabel: 'Dependencies',
  dependenciesUnavailable:
    "We couldn't set this up automatically here — add a column from the column header menu instead.",
  foundBadge: '✓ Found',
  matchedSuffix: '  ✓ matched',
  cancel: 'Cancel',
  apply: 'Apply',
  getStarted: 'Get started',
  createNewColumn: '+ Create new column',
  newColumnNamePlaceholder: 'Column name',
  chooseOrCreatePlaceholder: 'Choose or create a column…',
  autoCreateLabel: defaultName => `New “${defaultName}” column`,
};

type Mode = 'auto' | 'existing';

/**
 * Preset labels seeded into a wizard-created Status column store the same
 * select palette as user-created options ({@link getTagColor}), so "Todo"
 * chips and user-added chips share one visual palette.
 */
// Semantic colour pairing for the four seeded statuses. Chosen so a
// glance at a bar / chip communicates the task stage before the user
// reads the label. Primary brand blue is intentionally avoided here —
// status chips are a semantic overlay, not a brand surface; reusing
// primary blue would blur the "this is a status pill" affordance with
// the app's chrome.
//
//   Todo         → orange  (fresh, "pick me up" warm tone)
//   In Progress  → blue    (active flow, calm focus — chip/label hue,
//                          deliberately NOT the brand primary)
//   Blocked      → magenta (attention-grabbing violet-pink that
//                          doesn't collide with a red "error" tone)
//   Done         → green   (completion, closure — the semantic "ok")

/**
 * Toggle-driven wizard. Every section has a default — "we'll create a
 * column for you" — and a toggle that flips into "pick an existing
 * column" mode, progressively disclosing the dropdown(s). When the
 * name+type heuristics find an obvious match in the database schema,
 * the toggle starts ON with the match pre-selected so the user just
 * skims and hits Apply.
 *
 * The dates section treats mode=existing in three scan-based sub-flows:
 * zero date columns → toggle disabled (forced auto); one date column →
 * role picker (Start/End) + create the missing side; two or more →
 * two dropdowns. Progress and dependencies keep the flow flat: a single
 * dropdown when ON, a short auto-create hint when OFF.
 */
export class GanttSetupWizard extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @property({ attribute: false })
  accessor view!: GanttSingleView;

  @property({ attribute: false })
  accessor onClose: (() => void) | undefined;

  /**
   * Caller-provided localised copy. Missing keys fall back to the
   * English defaults; pass a full object to replace every string. Kept
   * partial so hosts can translate incrementally without breaking
   * rendering on an unknown key.
   */
  @property({ attribute: false })
  accessor strings: Partial<GanttWizardStrings> = {};

  @property({ attribute: false })
  accessor contained = false;

  @property({ attribute: false })
  accessor inline = false;

  private readonly startCol = signal<string | undefined>(undefined);
  private readonly endCol = signal<string | undefined>(undefined);
  private readonly progressCol = signal<string | undefined>(undefined);
  private readonly statusCol = signal<string | undefined>(undefined);
  private readonly assigneeCol = signal<string | undefined>(undefined);
  private readonly dependenciesCol = signal<string | undefined>(undefined);
  private readonly singleDateRole = signal<'start' | 'end'>('start');

  // Inline "create new column" draft per picker. `null` = picker in
  // regular mode; a string (even empty) = picker has switched into
  // the name-input flow. Apply treats non-null → create with that
  // name (uses the default name if blank).
  private readonly startCreating = signal<string | null>(null);
  private readonly endCreating = signal<string | null>(null);
  private readonly progressCreating = signal<string | null>(null);
  private readonly statusCreating = signal<string | null>(null);
  private readonly assigneeCreating = signal<string | null>(null);
  private readonly dependenciesCreating = signal<string | null>(null);

  /**
   * Per-section mode. `auto` means the wizard will create the column(s)
   * on Apply; `existing` means the user wants to wire an existing
   * column and the expandable body is visible. Initialised from the
   * detected matches in {@link connectedCallback}: a match flips the
   * toggle ON automatically so pre-configured schemas breeze through.
   */
  private readonly datesMode = signal<Mode>('auto');
  private readonly advancedExpanded = signal(false);

  override connectedCallback(): void {
    super.connectedCallback();

    const detected = ColumnDetectionService.scan(this.view);

    // Pre-seed dropdown selections from either the already-configured
    // view state (user opened wizard to tweak) or the detected matches
    // (first-time setup on a schema that has sensibly named columns).
    this.startCol.value =
      this.view.startDateCol$.value ?? detected.match.start?.id;
    this.endCol.value = this.view.endDateCol$.value ?? detected.match.end?.id;
    this.progressCol.value =
      this.view.progressCol$.value ?? detected.match.progress?.id;
    this.statusCol.value =
      this.view.statusCol$.value ?? detected.match.status?.id;
    this.assigneeCol.value =
      this.view.assigneeCol$.value ?? detected.match.assignee?.id;
    this.dependenciesCol.value =
      this.view.dependenciesCol$.value ?? detected.match.dependencies?.id;

    // Mode = 'existing' when the view was already configured (editing
    // an existing gantt) or when the heuristic found a match. Otherwise
    // default to 'auto' — user can flip the toggle if they want to pick.
    const configuredDates =
      this.view.startDateCol$.value || this.view.endDateCol$.value;
    const hasDateMatch = Boolean(detected.match.start || detected.match.end);
    this.datesMode.value =
      configuredDates || (detected.date.length > 0 && hasDateMatch)
        ? 'existing'
        : 'auto';

    // For the one-date case, pick the role from the name match so the
    // radio reflects the detected role by default.
    if (detected.date.length === 1 && detected.match.end) {
      this.singleDateRole.value = 'end';
    }

    this.disposables.addFromEvent(this, 'keydown', e => {
      if ((e as KeyboardEvent).key === 'Escape') this.close();
    });
  }

  private t(): GanttWizardStrings {
    return { ...DEFAULT_STRINGS, ...this.strings };
  }

  override render(): TemplateResult {
    const detected = ColumnDetectionService.scan(this.view);
    const t = this.t();
    const hasAnyDates = detected.date.length > 0;
    if (this.inline) {
      return html`
        <section
          class=${wizardInlineContainerStyle}
          data-data-view-body-content="true"
          data-data-view-body-intrinsic="true"
          data-data-view-body-render-content="true"
          data-data-view-body-render-root="true"
          role="region"
          aria-label=${t.title}
        >
          ${this.renderContent(detected, t, hasAnyDates)}
        </section>
      `;
    }

    const containerStyle = this.contained
      ? wizardContainedBackdropStyle
      : wizardBackdropStyle;
    const dialogStyle = this.contained
      ? `${wizardDialogStyle} ${wizardContainedDialogStyle}`
      : wizardDialogStyle;

    return html`
      <div class=${containerStyle}>
        <div
          class=${dialogStyle}
          role="dialog"
          aria-modal="true"
          aria-label=${t.title}
          @click=${(e: Event) => e.stopPropagation()}
        >
          ${this.renderContent(detected, t, hasAnyDates)}
        </div>
      </div>
    `;
  }

  private renderContent(
    detected: DetectedColumns,
    t: GanttWizardStrings,
    hasAnyDates: boolean
  ): TemplateResult {
    return html`
      <header class=${wizardHeroStyle}>
        <h1 class=${wizardTitleStyle}>${t.title}</h1>
        <p class=${wizardDescriptionStyle}>${t.description}</p>
      </header>

      <p class=${wizardHintStyle}>${t.automaticSummary}</p>
      ${this.renderAdvancedSettings(detected, t)}

      <div class=${wizardActionsStyle}>
        <button
          class=${wizardSecondaryButtonStyle}
          @click=${() => this.close()}
        >
          ${t.cancel}
        </button>
        <button
          class=${wizardPrimaryButtonStyle}
          ?disabled=${!this.canApply(detected)}
          @click=${() => this.apply(detected)}
        >
          ${hasAnyDates ? t.apply : t.getStarted}
        </button>
      </div>
    `;
  }

  private renderAdvancedSettings(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const expanded = this.advancedExpanded.value;
    return html`
      <button
        class=${wizardAdvancedTriggerStyle}
        type="button"
        aria-expanded=${expanded ? 'true' : 'false'}
        @click=${() => {
          this.advancedExpanded.value = !expanded;
        }}
      >
        <span class=${wizardAdvancedHeaderTextStyle}>
          <span class=${wizardSectionTitleStyle}>${t.advancedTitle}</span>
          <span class=${wizardSectionHintStyle}>${t.advancedDescription}</span>
        </span>
        <span
          class=${wizardDisclosureButtonStyle}
          data-open=${expanded ? 'true' : 'false'}
          aria-hidden="true"
        >
          ${this.renderDisclosureChevronIcon()}
        </span>
      </button>
      ${this.renderExpandableBody(
        expanded,
        () => html`
          ${this.renderDatesSection(detected, t)}
          ${this.renderTrackingSection(detected, t)}
          ${this.renderDependenciesSection(detected, t)}
        `
      )}
    `;
  }

  private renderDisclosureChevronIcon(): TemplateResult {
    return html`
      <svg width="16" height="16" viewBox="0 0 16 16" focusable="false">
        <path
          d="M6 4l4 4-4 4"
          stroke="currentColor"
          stroke-width="1.8"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  private renderExpandableBody(
    expanded: boolean,
    renderBody: () => TemplateResult
  ): TemplateResult {
    return html`
      <div
        class=${wizardExpandableBodyStyle}
        data-open=${expanded ? 'true' : 'false'}
        aria-hidden=${expanded ? 'false' : 'true'}
        ?inert=${!expanded}
      >
        <div class=${wizardExpandableInnerStyle}>
          ${expanded ? renderBody() : nothing}
        </div>
      </div>
    `;
  }

  protected override updated(): void {
    this.dispatchEvent(
      new CustomEvent(GANTT_SETUP_WIZARD_LAYOUT_CHANGE_EVENT, {
        bubbles: true,
        composed: true,
      })
    );
  }

  // ── Dates section ────────────────────────────────────────────────

  private renderDatesSection(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const hasDates = detected.date.length > 0;
    const mode = this.datesMode.value;

    return html`
      <section class=${wizardSectionStyle}>
        ${this.renderSectionHeader(t.datesTitle, {
          on: mode === 'existing',
          disabled: !hasDates,
          onChange: (next: boolean) =>
            (this.datesMode.value = next ? 'existing' : 'auto'),
        })}
        ${mode === 'existing' && hasDates
          ? this.renderDatesBody(detected, t)
          : html`<p class=${wizardHintStyle}>${t.datesAutoHint}</p>`}
      </section>
    `;
  }

  private renderDatesBody(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const count = detected.date.length;
    if (count === 1) return this.renderOneDateBody(detected, t);
    return this.renderManyDatesBody(detected, t);
  }

  private renderOneDateBody(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const col = detected.date[0];
    if (!col) return html``;
    const role = this.singleDateRole.value;
    const missing = role === 'start' ? t.roleEnd : t.roleStart;
    return html`
      <p class=${wizardSectionHintStyle}>${t.oneDateHint(col.name$.value)}</p>
      <div class=${wizardChoiceRowStyle}>
        <label class=${wizardChoiceCardStyle} data-selected=${role === 'start'}>
          <input
            type="radio"
            name="role"
            value="start"
            ?checked=${role === 'start'}
            @change=${() => (this.singleDateRole.value = 'start')}
          />
          <span>${t.roleStart}</span>
        </label>
        <label class=${wizardChoiceCardStyle} data-selected=${role === 'end'}>
          <input
            type="radio"
            name="role"
            value="end"
            ?checked=${role === 'end'}
            @change=${() => (this.singleDateRole.value = 'end')}
          />
          <span>${t.roleEnd}</span>
        </label>
      </div>
      <p class=${wizardSectionHintStyle}>${t.missingColumnNote(missing)}</p>
    `;
  }

  private renderManyDatesBody(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const matchedIds = new Set<string>(
      [detected.match.start?.id, detected.match.end?.id].filter(
        (id): id is string => !!id
      )
    );
    return html`
      ${this.renderSimplePicker(
        t.startLabel,
        this.startCol,
        this.startCreating,
        detected.date,
        t,
        matchedIds
      )}
      ${this.renderSimplePicker(
        t.endLabel,
        this.endCol,
        this.endCreating,
        detected.date,
        t,
        matchedIds
      )}
    `;
  }

  // ── Progress section ─────────────────────────────────────────────

  private renderTrackingSection(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    return html`
      <section class=${wizardSectionStyle}>
        <h2 class=${wizardSectionTitleStyle}>${t.trackingTitle}</h2>
        ${this.renderTrackingRow(
          t.progressColumnLabel,
          'Progress',
          this.progressCol,
          this.progressCreating,
          detected.progress,
          detected.match.progress,
          t
        )}
        ${this.renderTrackingRow(
          t.statusColumnLabel,
          'Status',
          this.statusCol,
          this.statusCreating,
          detected.status,
          detected.match.status,
          t
        )}
        ${this.renderTrackingRow(
          t.assigneeColumnLabel,
          'Assignee',
          this.assigneeCol,
          this.assigneeCreating,
          detected.assignee,
          detected.match.assignee,
          t
        )}
      </section>
    `;
  }

  private renderTrackingRow(
    label: string,
    defaultName: string,
    picked: { value: string | undefined },
    creating: { value: string | null },
    options: readonly { id: string; name$: { value: string } }[],
    match: { id: string } | undefined,
    t: GanttWizardStrings
  ): TemplateResult {
    return html`
      <div class=${wizardTrackingRowStyle}>
        ${this.renderRequiredPicker(
          label,
          defaultName,
          picked,
          creating,
          options,
          match?.id,
          t
        )}
      </div>
    `;
  }

  private renderRequiredPicker(
    label: string,
    defaultName: string,
    bound: { value: string | undefined },
    creating: { value: string | null },
    options: readonly { id: string; name$: { value: string } }[],
    matchedId: string | undefined,
    t: GanttWizardStrings
  ): TemplateResult {
    if (creating.value !== null) {
      return this.renderCreateInput(label, creating, t);
    }
    const matchedIds = matchedId ? new Set<string>([matchedId]) : undefined;
    const selectedLabel = bound.value
      ? (options.find(o => o.id === bound.value)?.name$.value ??
        t.chooseOrCreatePlaceholder)
      : t.autoCreateLabel(defaultName);
    const isMuted = !bound.value;
    const isMatchApplied = bound.value === matchedId && matchedId != null;
    return html`
      <div class=${wizardFieldStyle}>
        <label class=${wizardLabelStyle}>
          <span>${label}</span>
          ${isMatchApplied
            ? html`<span class=${wizardFoundBadgeStyle}>${t.foundBadge}</span>`
            : nothing}
        </label>
        <button
          class=${wizardSelectStyle}
          type="button"
          @click=${(e: MouseEvent) =>
            this.openPickerMenu(e, bound, creating, options, matchedIds, t)}
        >
          <span class=${isMuted ? wizardSelectPlaceholderStyle : ''}>
            ${selectedLabel}
          </span>
          <svg viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    `;
  }

  private renderCreateInput(
    label: string,
    creating: { value: string | null },
    t: GanttWizardStrings
  ): TemplateResult {
    return html`
      <div class=${wizardFieldStyle}>
        <label class=${wizardLabelStyle}>${label}</label>
        <div class=${wizardCreateInputRowStyle}>
          <input
            class=${wizardCreateInputStyle}
            type="text"
            autofocus
            .value=${creating.value ?? ''}
            placeholder=${t.newColumnNamePlaceholder}
            @input=${(e: Event) => {
              creating.value = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                creating.value = null;
              }
            }}
          />
          <button
            class=${wizardCreateCancelStyle}
            type="button"
            aria-label=${t.cancel}
            @click=${() => {
              creating.value = null;
            }}
          >
            ×
          </button>
        </div>
      </div>
    `;
  }

  // ── Dependencies section ─────────────────────────────────────────

  private renderDependenciesSection(
    detected: DetectedColumns,
    t: GanttWizardStrings
  ): TemplateResult {
    const hasExisting = detected.relation.length > 0;
    const canCreate = detected.selfBlockId !== '';
    const match = detected.match.dependencies;

    if (!hasExisting && !canCreate) {
      return html`
        <section class=${wizardSectionStyle}>
          ${this.renderSectionHeader(t.dependenciesTitle, {
            on: false,
            disabled: true,
            onChange: () => {},
            description: t.dependenciesDescription,
          })}
          <p class=${wizardHintStyle}>${t.dependenciesUnavailable}</p>
        </section>
      `;
    }

    return html`
      <section class=${wizardSectionStyle}>
        <div>
          <h2 class=${wizardSectionTitleStyle}>${t.dependenciesTitle}</h2>
          <p class=${wizardSectionHintStyle}>${t.dependenciesDescription}</p>
        </div>
        ${hasExisting
          ? this.renderRequiredPicker(
              t.dependenciesColumnLabel,
              'Dependencies',
              this.dependenciesCol,
              this.dependenciesCreating,
              detected.relation,
              match?.id,
              t
            )
          : html`<p class=${wizardHintStyle}>${t.dependenciesAutoHint}</p>`}
      </section>
    `;
  }

  // ── Shared controls ─────────────────────────────────────────────

  private renderSectionHeader(
    title: string,
    toggle: {
      on: boolean;
      disabled: boolean;
      onChange: (next: boolean) => void;
      description?: string;
    }
  ): TemplateResult {
    return html`
      <div class=${wizardSectionHeaderStyle}>
        <div>
          <h2 class=${wizardSectionTitleStyle}>${title}</h2>
          ${toggle.description
            ? html`<p class=${wizardSectionHintStyle}>${toggle.description}</p>`
            : nothing}
        </div>
        ${toggle.disabled
          ? nothing
          : this.renderToggle(toggle.on, false, toggle.onChange)}
      </div>
    `;
  }

  private renderToggle(
    on: boolean,
    disabled: boolean,
    onChange: (next: boolean) => void
  ): TemplateResult {
    return html`
      <span
        class=${wizardToggleStyle}
        data-on=${on ? 'true' : 'false'}
        data-disabled=${disabled ? 'true' : 'false'}
      >
        <input
          type="checkbox"
          ?checked=${on}
          ?disabled=${disabled}
          @change=${(e: Event) =>
            onChange((e.target as HTMLInputElement).checked)}
        />
        <span class=${wizardToggleThumbStyle}></span>
      </span>
    `;
  }

  /**
   * Custom dropdown — replaces `<select>` so the options render with
   * our tokens (platform native popovers show up as dark-grey on
   * macOS which clashes with the wizard's light card). Clicking the
   * button opens a popover menu listing every column; the last entry
   * is always "+ Create new column", which flips this picker into an
   * inline naming flow (`creating.value` holds the typed name).
   */
  private renderSimplePicker(
    label: string,
    bound: { value: string | undefined },
    creating: { value: string | null },
    options: readonly { id: string; name$: { value: string } }[],
    t: GanttWizardStrings,
    matchedIds?: ReadonlySet<string>
  ): TemplateResult {
    if (creating.value !== null) {
      return this.renderCreateInput(label, creating, t);
    }

    const selectedLabel = bound.value
      ? (options.find(o => o.id === bound.value)?.name$.value ??
        t.chooseColumnPlaceholder)
      : t.chooseOrCreatePlaceholder;
    const isPlaceholder = bound.value == null;
    const isMatchApplied = Boolean(bound.value && matchedIds?.has(bound.value));

    return html`
      <div class=${wizardFieldStyle}>
        <label class=${wizardLabelStyle}>
          <span>${label}</span>
          ${isMatchApplied
            ? html`<span class=${wizardFoundBadgeStyle}>${t.foundBadge}</span>`
            : nothing}
        </label>
        <button
          class=${wizardSelectStyle}
          type="button"
          @click=${(e: MouseEvent) =>
            this.openPickerMenu(e, bound, creating, options, matchedIds, t)}
        >
          <span class=${isPlaceholder ? wizardSelectPlaceholderStyle : ''}>
            ${selectedLabel}
          </span>
          <svg viewBox="0 0 10 6" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              stroke-width="1.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    `;
  }

  private openPickerMenu(
    evt: MouseEvent,
    bound: { value: string | undefined },
    creating: { value: string | null },
    options: readonly { id: string; name$: { value: string } }[],
    matchedIds: ReadonlySet<string> | undefined,
    t: GanttWizardStrings
  ): void {
    const target = evt.currentTarget as HTMLElement;
    const items = options.map(o =>
      menu.action({
        name: `${o.name$.value}${matchedIds?.has(o.id) ? t.matchedSuffix : ''}`,
        isSelected: o.id === bound.value,
        select: () => {
          bound.value = o.id;
          creating.value = null;
        },
      })
    );
    items.push(
      menu.action({
        name: t.createNewColumn,
        select: () => {
          bound.value = undefined;
          creating.value = '';
        },
      })
    );
    popFilterableSimpleMenu(popupTargetFromElement(target), items);
  }

  // ── Apply ───────────────────────────────────────────────────────

  private canApply(detected: DetectedColumns): boolean {
    const datesOk = this.isDatesReady(detected);
    const depsOk =
      Boolean(detected.selfBlockId) || Boolean(this.dependenciesCol.value);
    return datesOk && depsOk;
  }

  private isDatesReady(detected: DetectedColumns): boolean {
    if (this.datesMode.value === 'auto') return true;
    const count = detected.date.length;
    if (count === 0) return false;
    if (count === 1) return true;
    return true;
  }

  private apply(detected: DetectedColumns): void {
    applyGanttSetup(
      this.view,
      {
        datesMode: this.datesMode.value,
        singleDateRole: this.singleDateRole.value,
        columns: {
          start: this.startCol.value,
          end: this.endCol.value,
          progress: this.progressCol.value,
          status: this.statusCol.value,
          assignee: this.assigneeCol.value,
          dependencies: this.dependenciesCol.value,
        },
        createNames: {
          start: this.startCreating.value,
          end: this.endCreating.value,
          progress: this.progressCreating.value,
          status: this.statusCreating.value,
          assignee: this.assigneeCreating.value,
          dependencies: this.dependenciesCreating.value,
        },
        dependencies:
          detected.selfBlockId !== '' || detected.relation.length > 0,
      },
      detected
    );
    void this.view.manager.dataSource
      .flushLocalWrites()
      .then(() => this.close())
      .catch(console.error);
  }

  private close(): void {
    this.onClose?.();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-setup-wizard': GanttSetupWizard;
  }
}
